// react_frontend/src/Components/Modules/Billing/AddBilling.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import moment from "moment";
import Swal from "sweetalert2";
// RSuite Components
import {
  Button,
  Input,
  SelectPicker,
  Badge,
  Loader,
  Message,
  Modal,
  toaster,
  Tag,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
// Icons
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaCalendarAlt,
  FaUser,
  FaTrashAlt,
  FaPrint,
  FaBox,
} from "react-icons/fa";

const AddBilling = () => {
  const [data, setData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [cart, setCart] = useState([]);
  const [formDataPass, setFormDataPass] = useState({});
  const [currency, setCurrency] = useState("BDT");
  const [vat, setVat] = useState({});
  const [point, setPoint] = useState(0);
  const [points, setPoints] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [modalData, setModalData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [company, setCompany] = useState({});
  const [companyForPrint, setCompanyForPrint] = useState({});
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [finding, setFinding] = useState(false);
  const [quantityErrors, setQuantityErrors] = useState({});
  const [localQuantities, setLocalQuantities] = useState({});
  const [localDiscount, setLocalDiscount] = useState(0);
  const [localPoints, setLocalPoints] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [originalQuantities, setOriginalQuantities] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const findRef = useRef();
  const nameRef = useRef();
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("who_am_i") || 0);
  const isAdmin = userId === 1;
  const canEditDate = userId === 1 || userId === 2;
  const barcodeInputRef = useRef();

  // Fetch Data
  useEffect(() => {
    const loadData = async () => {
      try {
        const profileRes = await AxiosAuthInstance.get("company/profile");
        const companyData = profileRes.data.company || {};
        setCompany(companyData);
        setCurrency(companyData.currency || "BDT");
        setVat(companyData);

        const categoryRes = await AxiosAuthInstance.get("add_category");
        setData(categoryRes.data.category_filter || []);

        const InventoryRes = await AxiosAuthInstance.get("inventories");
        setInventoryData(InventoryRes.data.transactions || []);

        const countRes = await AxiosAuthInstance.post("count_total_order");
        setOrderCount(countRes.data.total_orders || 0);

        // ✅ Load ALL available products from all categories
        const allProductsRes = await AxiosAuthInstance.get("get_all_available_products");
        const productsWithCode = (allProductsRes.data || []).filter(p =>
          p.product_code != null && p.product_code.toString().trim() !== ""
        );
        setAllProducts(productsWithCode)

      } catch (error) {
        console.error("Failed to load ", error);
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to load required data.
          </Message>,
          { placement: "topEnd" }
        );
      }
    };
    loadData();
  }, []);

  // ✅ Helper function to get available quantity for an item
  const getAvailableQuantity = (itemId) => {
    // First, check if item is in cart with available_quantity
    const cartItem = cart.find(c => c.item_id === itemId);
    if (cartItem && cartItem.available_quantity !== undefined) {
      return cartItem.available_quantity;
    }

    // Fallback to inventoryData
    const invItems = inventoryData.filter(inv => inv.item_id === itemId);
    return invItems.reduce((sum, inv) => sum + (inv.hasProductForSelling || 0), 0);
  };

  // Debounce Hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedDiscount = useDebounce(localDiscount, 500);
  const debouncedPoints = useDebounce(localPoints, 500);
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    if (debouncedSearch.trim() === "") {
      setSearchResults([]);
      return;
    }
    const fetchSearchResults = async () => {
      setIsSearching(true);
      try {
        const res = await AxiosAuthInstance.get(`search_products?query=${debouncedSearch}`);
        setSearchResults(res.data || []);
      } catch (error) {
        console.error("Search failed:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    fetchSearchResults();
  }, [debouncedSearch]);

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId || null);
    if (!categoryId) {
      setItemData([]);
      return;
    }
    try {
      const res = await AxiosAuthInstance.get(`get_available_product/${categoryId}`);
      setItemData(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      setItemData([]);
    }
  };

  // Filter Products
  const filteredProducts = search.trim() !== ""
    ? searchResults
    : itemData;

  // Add to Cart - ✅ FIXED
  const handleAddToCart = (item) => {
    const availableQty = Number(item.available_quantity || 0);
    if (availableQty === 0) {
      toaster.push(
        <Message type="warning" duration={3000}>
          Out of stock: {item.item_name}
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }
    const existingIndex = cart.findIndex((c) => c.id === item.product_id);
    if (existingIndex >= 0) {
      const currentQty = cart[existingIndex].quantity;
      if (currentQty >= availableQty) {
        toaster.push(
          <Message type="warning" duration={3000}>
            Max stock reached for {item.item_name}
          </Message>,
          { placement: "topEnd" }
        );
        return;
      }
      const updated = [...cart];
      updated[existingIndex].quantity += 1;
      setCart(updated);
    } else {
      setCart([
        ...cart,
        {
          id: item.product_id,
          item_id: item.item_id,
          item_name: item.item_name,
          product_unit: item.product_unit,
          price: item.product_unit_price,
          available_quantity: availableQty,
          quantity: 1,
        },
      ]);
    }
  };

  // BARCODE SCAN - ✅ FIXED
  useEffect(() => {
    const handleBarcodeScan = (e) => {
      if (e.key === "Enter" && barcodeInputRef.current === document.activeElement) {
        e.preventDefault();

        const code = barcodeInputRef.current.value.trim();
        if (!code) return;

        const found = allProducts.find(
          (p) => String(p.product_code).trim() === code
        );

        if (!found) {
          toaster.push(
            <Message type="error" duration={2500}>
              Product not found
            </Message>,
            { placement: "topEnd" }
          );
          barcodeInputRef.current.value = "";
          return;
        }

        const availableQty = Number(found.available_quantity || 0);

        setCart((prevCart) => {
          const existingIndex = prevCart.findIndex(
            (c) => c.id === found.product_id
          );

          if (existingIndex >= 0) {
            const updated = [...prevCart];
            const currentQty = updated[existingIndex].quantity;

            if (currentQty >= availableQty) {
              toaster.push(
                <Message type="warning" duration={2500}>
                  Max stock reached for {found.item_name}
                </Message>,
                { placement: "topEnd" }
              );
              return prevCart;
            }

            updated[existingIndex].quantity += 1;
            updated[existingIndex].product_unit = found.product_unit;
            toaster.push(
              <Message type="success" duration={2000}>
                +1: {found.item_name}
              </Message>,
              { placement: "topEnd" }
            );
            return updated;
          }

          // Not found in cart — add new
          toaster.push(
            <Message type="success" duration={2000}>
              Added: {found.item_name}
            </Message>,
            { placement: "topEnd" }
          );
          return [
            ...prevCart,
            {
              id: found.product_id,
              item_id: found.item_id,
              item_name: found.item_name,
              product_unit: found.product_unit,
              price: found.product_unit_price,
              available_quantity: availableQty,
              quantity: 1,
            },
          ];
        });

        barcodeInputRef.current.value = "";
      }
    };

    window.addEventListener("keydown", handleBarcodeScan);
    return () => window.removeEventListener("keydown", handleBarcodeScan);
  }, [allProducts]);

  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);


  // Update Cart Quantity - ✅ FIXED
  const handleCartQuantityChange = (id, value) => {
    const numValue = value === "" ? "" : Number(value);

    // update typing state
    setLocalQuantities(prev => ({ ...prev, [id]: numValue }));

    // Find item from cart
    const item = cart.find(c => c.id === id);
    if (!item) return;

    const availableQty = Number(item.available_quantity || 0);

    // Empty বা invalid হলে শুধু UI তে দেখাও, state এ না পাঠাও
    if (value === "" || isNaN(numValue) || numValue < 1) return;

    // ✅ Stock validation
    if (numValue > availableQty) {
      toaster.push(
        <Message type="warning" duration={2500}>
          Max stock reached for {item.item_name} ({availableQty})
        </Message>,
        { placement: "topEnd" }
      );

      // revert to max available
      setLocalQuantities(prev => ({ ...prev, [id]: availableQty }));
      setCart(prev =>
        prev.map(i =>
          i.id === id ? { ...i, quantity: availableQty } : i
        )
      );
      return;
    }

    // ✅ Valid quantity — update cart
    setCart(prev =>
      prev.map(i =>
        i.id === id ? { ...i, quantity: numValue } : i
      )
    );
  };

  // Remove from Cart
  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter((c) => c.id !== itemId));
  };

  // Calculate Totals
  const total = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const vatAmount = (total * (vat.vat || 0)) / 100;
  const netTotal = total + vatAmount;
  const discountValue = parseFloat(count) || 0;
  const pointValue = parseFloat(point) || 0;
  const finalTotal = Math.max(0, netTotal - discountValue - pointValue);

  // Format Currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "BDT",
      minimumFractionDigits: 2,
    }).format(value);
  };

  // View Existing Billing
  const handleView = async () => {
    const orderNo = findRef.current?.value.trim();
    if (!orderNo) return;
    setFinding(true);
    try {
      const response = await AxiosAuthInstance.get(`getBillingByOrderNo/${orderNo}`);
      const billing = response.data.billing;
      if (billing) {

        const originals = {};
        billing.billing_items?.forEach(item => {
          originals[item.id] = item.quantity;
        });
        setOriginalQuantities(originals);

        const customerName = billing.customer?.name || billing.customer_name || "Walk-in Customer";
        const customerPhone = billing.customer?.phone || billing.customer_phone || "";
        const billingWithCustomer = {
          ...billing,
          customer_name: customerName,
          customer_phone: customerPhone,
        };
        setModalData(billingWithCustomer);
        setFormDataPass(billingWithCustomer);

        const companyRes = await AxiosAuthInstance.get("company/profile");
        setCompanyForPrint(companyRes.data.company || {});

        setModalOpen(true);
        findRef.current.value = "";
      } else {
        toaster.push(
          <Message type="info" duration={3000}>
            No billing found for this order number.
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (err) {
      console.error("Failed to load billing:", err);
      toaster.push(
        <Message type="error" duration={5000}>
          Failed to load billing.
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setFinding(false);
    }
  };

  // Get Customer by Phone
  const getCustomerNumber = async (phone) => {
    if (!phone) {
      if (nameRef.current) nameRef.current.value = "";
      setPoints(0);
      if (modalData) {
        setModalData((prev) => ({ ...prev, customer_name: "" }));
      }
      return;
    }
    setLoadingCustomer(true);
    try {
      const response = await AxiosAuthInstance.get(`customer/${phone}`);
      const customer = response.data.customer;
      if (customer) {
        if (nameRef.current) nameRef.current.value = customer.name;
        if (modalData) {
          setModalData((prev) => ({ ...prev, customer_name: customer.name }));
        }
        setPoints(customer.points || 0);
        toaster.push(
          <Message type="success" duration={2000}>
            Customer loaded: {customer.name}
          </Message>,
          { placement: "topEnd" }
        );
      } else {
        if (nameRef.current) nameRef.current.value = "Walk-in";
        if (modalData) {
          setModalData((prev) => ({ ...prev, customer_name: "Walk-in" }));
        }
        setPoints(0);
      }
    } catch (err) {
      console.error("Failed to load customer:", err);
      if (nameRef.current) nameRef.current.value = "Error";
      if (modalData) {
        setModalData((prev) => ({ ...prev, customer_name: "Error" }));
      }
      setPoints(0);
      toaster.push(
        <Message type="error" duration={3000}>
          Failed to load customer.
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setLoadingCustomer(false);
    }
  };

  // Handle Delete in Modal
  const handleDetailsDelete = async (item) => {
    if (!item?.id) {
      toaster.push(
        <Message type="error" duration={3000}>
          Invalid item ID.
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this item!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      const res = await AxiosAuthInstance.delete(`/deleteBillingItem/${item.id}`);
      const updatedBilling = res?.data?.billing;
      if (updatedBilling) {
        const preservedCustomerInfo = {
          customer_phone: modalData?.customer_phone || "",
          customer_name: modalData?.customer_name || "",
        };
        const normalized = {
          ...updatedBilling,
          billing_items: updatedBilling.billing_items || updatedBilling.billingItems || [],
          ...preservedCustomerInfo,
        };
        setModalData(normalized);
        setFormDataPass(normalized);
        toaster.push(
          <Message type="success" duration={2000}>
            Item deleted and billing updated.
          </Message>,
          { placement: "topEnd" }
        );
        return;
      }
      setModalData((prev) => ({
        ...prev,
        billing_items: prev?.billing_items?.filter((i) => i.id !== item.id) || [],
      }));
      setFormDataPass((prev) => ({
        ...prev,
        billing_items: prev?.billing_items?.filter((i) => i.id !== item.id) || [],
      }));
      toaster.push(
        <Message type="success" duration={2000}>
          Item removed locally.
        </Message>,
        { placement: "topEnd" }
      );
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.message || "Unknown error.";
      toaster.push(
        <Message type="error" duration={7000}>
          Failed to delete item: {serverMsg}
        </Message>,
        { placement: "topEnd" }
      );
    }
  };

  // Update Quantity in Modal - ✅ FIXED
  const handleDetailsQuantity = async (id, value) => {
    const numValue = value === "" ? "" : Number(value);

    const item = modalData?.billing_items?.find(i => i.id === id);
    if (!item) return;

    // ✅ Get available quantity using helper function
    const availableQty = getAvailableQuantity(item.item_id);
    const prevQty = item.quantity; // current quantity before change

    // invalid or blank
    if (!numValue || isNaN(numValue) || numValue < 1) {
      setQuantityErrors(prev => ({ ...prev, [id]: "Quantity must be at least 1" }));
      return;
    }

    // exceeded stock
    if (numValue > availableQty) {
      toaster.push(
        <Message type="warning" duration={2500}>
          Max stock reached for {item.item?.item_name || "item"} ({availableQty})
        </Message>,
        { placement: "topEnd" }
      );

      // revert to previous
      setLocalQuantities(prev => ({ ...prev, [id]: prevQty }));
      setModalData(prev => ({
        ...prev,
        billing_items: prev.billing_items.map(i =>
          i.id === id
            ? { ...i, quantity: prevQty, sub_total: prevQty * i.unit_price }
            : i
        ),
      }));
      return;
    }

    // ✅ Valid quantity — update instantly
    setQuantityErrors(prev => ({ ...prev, [id]: null }));
    setLocalQuantities(prev => ({ ...prev, [id]: numValue }));

    // UI update first
    setModalData(prev => ({
      ...prev,
      billing_items: prev.billing_items.map(i =>
        i.id === id
          ? { ...i, quantity: numValue, sub_total: numValue * i.unit_price }
          : i
      ),
    }));

    // ✅ DB Update (non-blocking)
    try {
      await AxiosAuthInstance.post(`/billingQuantityUpdate`, {
        quantity: numValue,
        id: id,
      });
      toaster.push(
        <Message type="success" duration={1500}>
          Quantity updated for {item.item?.item_name || "item"}
        </Message>,
        { placement: "topEnd" }
      );
    } catch (error) {
      console.error("Failed to save quantity:", error);
      toaster.push(
        <Message type="error" duration={2500}>
          Failed to update quantity. Reverting...
        </Message>,
        { placement: "topEnd" }
      );
      // revert UI
      setModalData(prev => ({
        ...prev,
        billing_items: prev.billing_items.map(i =>
          i.id === id
            ? { ...i, quantity: prevQty, sub_total: prevQty * i.unit_price }
            : i
        ),
      }));
      setLocalQuantities(prev => ({ ...prev, [id]: prevQty }));
    }
  };

  // Update Discount in Modal
  const handleDetailsDiscount = async (id, value) => {
    try {
      await AxiosAuthInstance.post(`/billingDiscountUpdate`, {
        total_discount: value,
        id,
      });
      const preservedCustomerInfo = {
        customer_phone: modalData?.customer_phone || "",
        customer_name: modalData?.customer_name || "",
      };
      setModalData((prev) => ({ ...prev, ...preservedCustomerInfo, total_discount: value }));
      setFormDataPass((prev) => ({ ...prev, ...preservedCustomerInfo, total_discount: value }));
    } catch (error) {
      console.error("Failed to update discount:", error);
    }
  };

  // Update Points in Modal
  const handleDetailsPoints = async (id, value) => {
    try {
      await AxiosAuthInstance.post(`/billingPointUpdate`, {
        total_points: value,
        id,
      });
      const preservedCustomerInfo = {
        customer_phone: modalData?.customer_phone || "",
        customer_name: modalData?.customer_name || "",
      };
      setModalData((prev) => ({ ...prev, ...preservedCustomerInfo, point_used: value }));
      setFormDataPass((prev) => ({ ...prev, ...preservedCustomerInfo, point_used: value }));
    } catch (error) {
      console.error("Failed to update points:", error);
    }
  };

  // Handle Form Submit
  // 🔹 Reusable Toast Helper
  const showToast = (type, message, duration = 3000) => {
    toaster.push(
      <Message type={type} duration={duration}>
        {message}
      </Message>,
      { placement: "topEnd" }
    );
  };

  // 🔹 Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🧩 Validation: Cart Empty
    if (cart.length === 0) {
      showToast("warning", "Add at least one item to cart.");
      return;
    }

    // 🧩 Prepare Form Data
    const formData = new FormData(e.target);
    formData.append("total_price", total);
    formData.append("net_total", netTotal);
    formData.append("total_amount_to_be_paid", finalTotal);

    cart.forEach((item) => {
      formData.append("item_id[]", item.item_id);
      formData.append("product_unit[]", item.product_unit);
      formData.append("product_id[]", item.id);
      formData.append("unit_price[]", item.price);
      formData.append("quantity[]", item.quantity);
      formData.append("sub_total[]", item.price * item.quantity);
    });

    setLoading(true);

    try {
      const res = await AxiosAuthInstance.post("billings", formData);
      // Success
      if (res.data.status === true) {
        const billing = res.data.billing;
        // Update order count BEFORE navigation
        setOrderCount((prev) => prev + 1);
        // Give React a moment to update state visually (optional but safe)
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (printAfterSave) {
          navigate("/print-receipt", { state: { billing, company } });
        } else {
          showToast("success", "Billing saved successfully!");
          navigate("/billing_list");
        }
      }
      //  Failure from server
      else {
        showToast("error", res.data.message || "Failed to save billing.", 5000);
      }
    } catch (err) {
      // Network / validation error
      const errorMsg = err.response?.data?.message || "Failed to save billing.";
      setErrors(err.response?.data?.errors || {});
      showToast("error", errorMsg, 5000);
    } finally {
      setLoading(false);
    }
  };


  // Calculate real-time totals from modalData (client-side)
  const calculateRealTimeTotals = (data) => {
    if (!data?.billing_items?.length)
      return {
        total: 0,
        vatAmount: 0,
        netTotal: 0,
        finalTotal: 0,
      };

    // ✅ Use localQuantities for real-time calculation
    const total = data.billing_items.reduce(
      (sum, item) => {
        const qty = localQuantities[item.id] ?? item.quantity; // ✅ Use local state
        return sum + (parseFloat(item.unit_price) || 0) * qty;
      },
      0
    );

    const vatPercent = parseFloat(vat.vat) || 0;
    const vatAmount = (total * vatPercent) / 100;
    const netTotal = total + vatAmount;

    const discountValue = parseFloat(data.total_discount) || 0;
    const pointValue = parseFloat(data.point_used) || 0;
    const finalTotal = Math.max(0, netTotal - discountValue - pointValue);

    return { total, vatAmount, netTotal, finalTotal };
  };


  useEffect(() => {
    if (modalData) {
      setLocalDiscount(modalData.total_discount || 0);
      setLocalPoints(modalData.point_used || 0);
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData?.id && debouncedDiscount !== undefined) {
      handleDetailsDiscount(modalData.id, debouncedDiscount);
    }
  }, [debouncedDiscount]);

  useEffect(() => {
    if (modalData?.id && debouncedPoints !== undefined) {
      handleDetailsPoints(modalData.id, debouncedPoints);
    }
  }, [debouncedPoints]);



  return (
    <>
      <Breadcrumb title="Add Billing" />
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm border-0">
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPlus /> Add New
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <Input
                placeholder="Order No"
                ref={findRef}
                onKeyDown={(e) => e.key === "Enter" && handleView()}
                style={{ width: 160 }}
                size="md"
              />
              <Button
                size="md"
                appearance="primary"
                startIcon={
                  finding ? <Loader size="sm" style={{ color: "white" }} /> : <FaSearch />
                }
                onClick={handleView}
                disabled={finding}
              >
                {finding ? "Finding..." : "Find"}
              </Button>
            </div>
          </div>
          <div className="card-body bg-white">
            <div className="row">
              {/* Left Side: Products */}
              <div className="col-xl-5 col-12">
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <SelectPicker
                      placeholder="Select Category"
                      data={data.map((cat) => ({ label: cat.category_name, value: cat.id }))}
                      onChange={handleCategoryChange}
                      value={selectedCategory}
                      style={{ width: "100%" }}
                      cleanable={true}
                    />
                  </div>
                  <div className="col-md-4">
                    <Input
                      placeholder="Search Product (Name/Code)"
                      value={search}
                      onChange={setSearch}
                      style={{ width: "100%" }}
                      cleanable
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // no form submit
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <Input
                      type="date"
                      name="billing_date"
                      defaultValue={moment().format("YYYY-MM-DD")}
                      readOnly={!isAdmin}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>

                  {/* 🔍 Barcode Input */}
                  <div className="mb-3">
                    <Input
                      id="barcodeInput"
                      ref={barcodeInputRef}
                      type="text"
                      placeholder="Scan or enter barcode..."
                      style={{ width: '100%' }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      autoFocus
                    />
                  </div>

                {/* Show loading or products */}
                {isSearching ? (
                  <div className="text-center py-3">Searching...</div>
                ) : filteredProducts.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    No product found
                  </div>
                ) : (
                  <div className="row pb-3 g-3">
                    {filteredProducts.map((p, i) => {
                      const available = Number(p.available_quantity);
                      const outOfStock = available === 0;
                      const lowStock = available > 0 && available <= 10;
                      return (
                        <div key={i} className="col-md-4 col-6">
                          <div
                            className={`card h-100 border ${outOfStock ? "border-danger" : lowStock ? "border-warning" : "border-secondary"}`}
                            style={{
                              cursor: outOfStock ? "not-allowed" : "pointer",
                              opacity: outOfStock ? 0.6 : 1,
                            }}
                            onClick={() => !outOfStock && handleAddToCart(p)}
                          >
                            <div className="card-body">
                              <h6 className="card-title">{p.item_name}</h6>
                              <p className="card-text">
                                <strong>Price:</strong> {formatCurrency(p.product_unit_price)}
                                {p.product_code && <small className="d-block">Code: {p.product_code}</small>}
                              </p>
                              <Badge
                                color={outOfStock ? "red" : lowStock ? "orange" : "green"}
                                content={`Stock: ${available}`}
                                style={{ fontSize: "0.8em" }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Right Side: Cart */}
              <div className="col-xl-7 col-12 border-start px-3">
                <div className="row mb-3 g-3">
                  <div className="col-md-6">
                    <Input
                      type="number"
                      name="order_number"
                      value={moment().format("YYYY") + (orderCount + 1)}
                      readOnly
                      placeholder="Order No"
                      size="md"
                      block
                    />
                  </div>
                  <div className="col-md-6">
                    <Input
                      name="customer_name"
                      ref={nameRef}
                      placeholder="Customer Name"
                      size="md"
                      readOnly
                      block
                    />
                  </div>
                </div>
                <div className="row mb-3 g-3">
                  <div className="col-md-6">
                    <Input
                      name="customer_phone"
                      placeholder="Customer Phone"
                      value={modalData?.customer_phone || ""}
                      onChange={(value) => {
                        setModalData((prev) => ({ ...prev, customer_phone: value }));
                        if (value.length > 1) getCustomerNumber(value);
                      }}
                      size="md"
                      block
                      suffix={
                        loadingCustomer ? <Loader size="sm" /> : points > 0 ? <Tag color="blue" size="sm">{points} pts</Tag> : null
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <SelectPicker
                      name="payment_method"
                      placeholder="Payment Method"
                      data={[
                        { label: "Cash", value: "cash" },
                        { label: "Card", value: "card" },
                        { label: "Mobile Banking", value: "mobile_banking" },
                        { label: "Mixed", value: "mixed" },
                      ]}
                      style={{ width: "100%" }}
                      cleanable={false}
                      searchable={false}
                    />
                  </div>
                </div>
                {/* Cart Items */}
                <div className="custom-overflow" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {cart.map((item, i) => (
                    <div key={item.id} className="card mb-2 border-0 border-bottom">
                      <div className="card-body addBillingproductlist d-flex align-items-center justify-content-between flex-wrap py-1">
                        <div className="col-4 d-flex align-items-start">
                          <span>{i + 1}.&nbsp; </span>
                          <h6 className="mb-0 w-100 lh-1">{item.item_name}</h6>
                        </div>
                        <div className="col-2">{formatCurrency(item.price)}</div>
                        <div className="col-2 d-flex align-items-center">
                          <Input
                            type="number"
                            value={localQuantities[item.id] ?? item.quantity}
                            onChange={(v) => handleCartQuantityChange(item.id, v)}
                            min={1}
                            style={{ width: "60px" }}
                          />
                          <small className="text-muted ms-2">({item.product_unit})</small>
                        </div>
                        <div className="col-3">{formatCurrency(item.price * item.quantity)}</div>
                        <div className="col-1">
                          <Button
                            size="sm"
                            color="red"
                            startIcon={<FaTrashAlt />}
                            onClick={() => handleRemoveFromCart(item.id)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Totals */}
                <div className="mt-4 text-end">
                  <p><strong>Total:</strong> {formatCurrency(total)}</p>
                  <p><strong>VAT ({vat.vat}%):</strong> {formatCurrency(vatAmount)}</p>
                  <p><strong>Net Total:</strong> {formatCurrency(netTotal)}</p>
                  <p><strong>Points Available:</strong> {points}</p>
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <span><strong>Discount:</strong></span>
                    <Input
                      type="number"
                      value={count}
                      onChange={setCount}
                      max={netTotal - pointValue}
                      name="total_discount"
                      style={{ width: 100 }}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <span><strong>Point Use:</strong></span>
                    <Input
                      type="number"
                      value={point}
                      onChange={setPoint}
                      max={points}
                      name="point"
                      style={{ width: 100 }}
                    />
                  </div>
                  {(discountValue > 0 || pointValue > 0) && (
                    <p className="mt-2"><strong>Final Total:</strong> {formatCurrency(finalTotal)}</p>
                  )}
                  <div className="mt-4 d-flex gap-2 justify-content-end">
                    <Button
                      className="custom-bg"
                      type="submit"
                      onClick={() => setPrintAfterSave(true)}
                      appearance="primary"
                      startIcon={<FaPrint />}
                      disabled={cart.length === 0 || loading}
                      style={{ minWidth: 140 }}
                    >
                      {loading ? "Saving..." : "Save & Print"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* View Modal */}
      <Modal
        open={modalOpen}
        size="lg"
        style={{ maxWidth: "100vw" }}
        onClose={() => setModalOpen(false)}
      >
        <Modal.Header className="border-bottom pb-2">
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <FaEye style={{ color: "#4351A3" }} />
              <span style={{ fontWeight: "600", color: "#333" }}>
                Billing Details (Order #{modalData?.order_number})
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="view-modal-body">
          {modalData?.billing_items?.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaBox size={48} className="mb-3 opacity-50" />
              <p>No items found in this billing.</p>
            </div>
          ) : (
            <>
              {/* Customer & Billing Info */}
              <div className="row mb-4 border-bottom pb-3">
                <div className="col-md-6">
                  <h6 className="fw-bold text-primary mb-3"><FaUser className="me-2" /> Customer Info</h6>
                  <div className="mb-2">
                    <label className="form-label"><small>Customer Phone</small></label>
                    <Input
                      name="customer_phone"
                      placeholder="Customer Phone"
                      value={modalData?.customer_phone || ""}
                      onChange={(value) => {
                        setModalData((prev) => ({ ...prev, customer_phone: value }));
                        if (value.length > 1) getCustomerNumber(value);
                      }}
                      size="md"
                      block
                      readOnly
                      suffix={
                        loadingCustomer ? <Loader size="sm" /> : points > 0 ? <Tag color="blue" size="sm">{points} pts</Tag> : null
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label"><small>Customer Name</small></label>
                    <Input
                      name="customer_name"
                      placeholder="Customer Name"
                      value={modalData?.customer_name || ""}
                      onChange={(e) => setModalData((prev) => ({ ...prev, customer_name: e.target.value }))}
                      size="md"
                      block
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-md-6 d-flex flex-column">
                  <h6 className="fw-bold text-success mb-3"><FaCalendarAlt className="me-2" /> Billing Info</h6>
                  <div className="mb-2">
                    <label className="form-label"><small>Billing Date</small></label>
                    <Input
                      type="date"
                      name="billing_date"
                      value={
                        modalData?.billing_date
                          ? modalData.billing_date.split("T")[0]
                          : new Date().toISOString().slice(0, 10)
                      }
                      onChange={(e) =>
                        setModalData((prev) => ({ ...prev, billing_date: e.target.value }))
                      }
                      size="md"
                      block
                      readOnly={!canEditDate}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label"><small>Payment Method</small></label>
                    <SelectPicker
                      name="payment_method"
                      placeholder="Payment Method"
                      data={[
                        { label: "Cash", value: "cash" },
                        { label: "Card", value: "card" },
                        { label: "Mobile Banking", value: "mobile_banking" },
                        { label: "Mixed", value: "mixed" },
                      ]}
                      value={modalData?.payment_method || "cash"}
                      onChange={(value) => setModalData((prev) => ({ ...prev, payment_method: value }))}
                      style={{ width: "100%" }}
                      cleanable={false}
                      searchable={false}
                    />
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="table-responsive mb-4">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center" style={{ width: "8%" }}>SL</th>
                      <th>Item Name</th>
                      <th className="text-end" style={{ width: "15%" }}>Unit Price</th>
                      <th className="text-center" style={{ width: "10%" }}>Qty</th>
                      <th className="text-center" style={{ width: "10%" }}>Unit</th>
                      <th className="text-end" style={{ width: "15%" }}>Sub Total</th>
                      <th className="text-center" style={{ width: "10%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData?.billing_items?.map((item, i) => {
                      const itemId = item.id || item.item_id;
                      const availableQty = getAvailableQuantity(item.item_id);
                      return (
                        <tr key={itemId || i}>
                          <td className="text-center"><strong>{i + 1}</strong></td>
                          <td className="fw-medium">{item.item?.item_name || "N/A"}</td>
                          <td className="text-end">{formatCurrency(item.unit_price)}</td>
                          <td className="text-center">
                            <Input
                              type="number"
                              min={1}
                              value={localQuantities[item.id] ?? item.quantity}
                              onChange={(value) => handleDetailsQuantity(item.id, value)}
                              style={{ width: "60px" }}
                            />
                            {quantityErrors[item.id] && (
                              <small className="text-danger d-block mt-1">
                                {quantityErrors[item.id]}
                              </small>
                            )}
                          </td>
                          <td className="text-center">{item.item?.product_unit || "N/A"}</td>
                          <td className="text-end fw-bold">{formatCurrency(item.sub_total)}</td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              color="red"
                              startIcon={<FaTrashAlt />}
                              onClick={() => handleDetailsDelete(item)}
                              style={{ padding: "4px 8px" }}
                              title="Delete item"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary Cards */}
              <div className="row g-3">
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Total Items</small>
                    <div className="h5 fw-bold">{modalData?.billing_items?.length || 0}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Sub Total</small>
                    <div className="h5 fw-bold text-success">
                      {formatCurrency(calculateRealTimeTotals(modalData).total)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">VAT</small>
                    <div className="h5 fw-bold text-primary">
                      {formatCurrency(calculateRealTimeTotals(modalData).vatAmount)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center d-flex flex-column align-items-center">
                    <small className="text-muted">Discount</small>
                    <div className="h5 fw-bold text-danger">
                      <Input
                        type="number"
                        value={localDiscount}
                        onChange={setLocalDiscount}
                        min={0}
                        max={netTotal - localPoints}
                        style={{ width: 100, textAlign: "center" }}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center d-flex flex-column align-items-center">
                    <small className="text-muted">Points Used</small>
                    <div className="h5 fw-bold text-warning">
                      <Input
                        type="number"
                        value={localPoints}
                        onChange={setLocalPoints}
                        min={0}
                        max={points}
                        style={{ width: 100, textAlign: "center" }}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="p-3 bg-light border rounded text-center">
                    <small className="text-muted">Payment</small>
                    <div className="h5 fw-bold">
                      <Badge
                        color={
                          modalData?.payment_method?.toLowerCase() === "cash"
                            ? "green"
                            : modalData?.payment_method?.toLowerCase() === "card"
                              ? "blue"
                              : "yellow"
                        }
                        style={{ fontSize: "0.9em" }}
                      >
                        {modalData?.payment_method || "N/A"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <div className="p-3 bg-success text-white border rounded text-center">
                    <small>Net Payable</small>
                    <div className="h4 fw-bold">
                      {formatCurrency(calculateRealTimeTotals(modalData).finalTotal)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end mt-3">
          <Button
            className="custom-bg"
            appearance="primary"
            startIcon={<FaPrint />}
            onClick={async () => {
              if (!modalData?.id) return;
              try {
                const formData = new FormData();
                formData.append("_method", "PUT");
                formData.append("customer_name", modalData.customer_name || "");
                formData.append("customer_phone", modalData.customer_phone || "");
                formData.append("billing_date", modalData.billing_date);
                formData.append("order_number", modalData.order_number);
                formData.append("total_price", modalData.total_price);
                formData.append("total_discount", modalData.total_discount || 0);
                formData.append("point_used", modalData.point_used || 0);
                formData.append("payment_method", modalData.payment_method);
                const response = await AxiosAuthInstance.post(`billings/${modalData.id}`, formData, {
                  headers: { "Content-Type": "multipart/form-data" },
                });
                if (response.data.status === true) {
                  navigate("/print-receipt", {
                    state: { billing: response.data.billing, company: companyForPrint },
                  });
                  setModalOpen(false);
                  toaster.push(
                    <Message type="success" duration={3000}>
                      Billing updated and ready to print!
                    </Message>,
                    { placement: "topEnd" }
                  );
                } else {
                  throw new Error(response.data.message || "Update failed");
                }
              } catch (error) {
                console.error("Update failed:", error);
                toaster.push(
                  <Message type="error" duration={5000}>
                    Failed to update billing.
                  </Message>,
                  { placement: "topEnd" }
                );
              }
            }}
            disabled={!modalData?.id}
          >
            Save & Print
          </Button>
          <Button className="border" appearance="subtle" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddBilling;